import type { Diagnosis, DocMove, RestoreStructureResult } from "../../api/repair";

type RenderHints = {
  movedMap?: Map<string, DocMove>;
  externalIds?: Set<string>;
  addedIds?: Set<string>;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function renderTreeNode(node: any, depth = 0, hints: RenderHints = {}): React.ReactNode {
  const indent = depth * 16;
  const isFolder = Array.isArray(node?.children);
  const added = !isFolder && hints.addedIds?.has(node?.id);
  const external = !isFolder && !added && hints.externalIds?.has(node?.id);
  // Folders can also move (backup may reparent them); highlight them too.
  const moved = !added && !external && hints.movedMap?.get(node?.id);

  const rowClass = isFolder
    ? moved
      ? "font-medium text-emerald-900 bg-emerald-50/80 rounded"
      : "font-medium text-gray-800"
    : added
    ? "text-emerald-900 bg-emerald-50/80 rounded font-medium"
    : external
    ? "text-red-800/90 bg-red-50/60 rounded line-through"
    : moved
    ? "text-emerald-900 bg-emerald-50/80 rounded"
    : "text-gray-600";

  const icon = isFolder ? "📁" : added ? "➕" : external ? "✕" : "📄";
  const suffix = added
    ? "will be restored"
    : external
    ? "will be removed"
    : moved
    ? `moved from ${moved.from}`
    : null;
  const suffixClass = added
    ? "text-emerald-700/80"
    : external
    ? "text-red-700/80 no-underline"
    : "text-emerald-700/80";

  return (
    <div key={node.id}>
      <div style={{ paddingLeft: indent }} className={`py-0.5 pr-2 flex items-center gap-1 ${rowClass}`}>
        <span className="text-xs">{icon}</span>
        <span className="truncate">{node.name || node.title || "Untitled"}</span>
        {suffix && (
          <span className={`ml-auto text-[10px] whitespace-nowrap font-normal ${suffixClass}`}>
            {suffix}
          </span>
        )}
      </div>
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {isFolder && node.children?.map((child: any) => renderTreeNode(child, depth + 1, hints))}
    </div>
  );
}

/**
 * Renders the full end-state tree after all three repair actions. The server
 * simulates the chain (injects missingDocs into the remap input), so each
 * missing doc already sits in its true final folder — we just highlight it.
 *
 * - `movedMap` from `result.moves` (includes both docs and folders)
 * - `addedIds` from `diagnosis.missingFromTree` (green "will be restored")
 * - `externalIds` from `diagnosis.externalDocs` (struck through "will be removed")
 */
export function RestoreStructureDetails({
  result,
  diagnosis,
}: {
  result: RestoreStructureResult;
  diagnosis?: Diagnosis | null;
}) {
  const moves = result.moves ?? [];
  const tree = result.tree ?? [];
  const rootDocs = result.rootDocs ?? [];
  const missingDocs = diagnosis?.missingFromTree ?? [];
  const externalIds = new Set<string>((diagnosis?.externalDocs ?? []).map((d) => d.id));
  const addedIds = new Set<string>(missingDocs.map((d) => d.id));

  const totalChanges = moves.length + missingDocs.length + externalIds.size;
  if (totalChanges === 0) {
    return (
      <p className="text-xs text-gray-500 italic">
        No structural changes — project already matches the backup.
      </p>
    );
  }

  const movedMap = new Map<string, DocMove>();
  for (const m of moves) movedMap.set(m.id, m);

  // A missing doc might show up as a "move" too (root → its backup folder).
  // That's technically accurate but we'd rather flag it as "added" since the
  // move happens because the doc wasn't there to begin with. Strip added IDs
  // from the move map.
  for (const id of addedIds) movedMap.delete(id);

  const hints: RenderHints = { movedMap, externalIds, addedIds };

  const summaryBits: string[] = [];
  if (movedMap.size > 0) summaryBits.push(`${movedMap.size} move${movedMap.size === 1 ? "" : "s"}`);
  if (addedIds.size > 0) summaryBits.push(`${addedIds.size} to restore`);
  if (externalIds.size > 0) summaryBits.push(`${externalIds.size} to remove`);

  return (
    <div className="space-y-1.5">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        Structure after repair — {summaryBits.join(" · ")}
      </p>
      <div className="text-xs max-h-72 overflow-auto border border-gray-200 rounded bg-white p-2">
        {tree.map((folder) => renderTreeNode(folder, 0, hints))}
        {rootDocs.map((doc) => renderTreeNode(doc, 0, hints))}
      </div>
    </div>
  );
}
